import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, Building2, Loader2 } from "lucide-react";
import api from "../api";
import { Helmet } from "react-helmet-async";
import { Button, Input, Textarea, Card, Badge } from "../components/ui";
import { useSuccessFailure } from "../context/SuccessFailureContext";

const CommunitiesPage = () => {
  const { showFailureModal } = useSuccessFailure();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/communities/workspaces/");
      setWorkspaces(res.data || []);
    } catch (e) {
      showFailureModal("Error", "Could not load your workspaces.");
    } finally {
      setLoading(false);
    }
  }, [showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/communities/workspaces/", form);
      setForm({ name: "", description: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      showFailureModal("Failed", "Could not create the workspace.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <Helmet><title>Workspaces | EventSawa</title></Helmet>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Workspaces</h1>
            <p className="text-gray-500 dark:text-gray-400">Workspaces for your event teams — rooms, tasks & metrics.</p>
          </div>
          <Button onClick={() => setShowForm((s) => !s)} leftIcon={<Plus size={16} />}>New workspace</Button>
        </div>

        {showForm && (
          <Card className="p-6 mb-6">
            <form onSubmit={create} className="space-y-3">
              <Input label="Workspace name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Gala 2026 Crew" />
              <Textarea label="Description" rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" loading={saving}>Create</Button>
              </div>
            </form>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        ) : workspaces.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            No workspaces yet. Create one to organize your event team.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {workspaces.map((w) => (
              <Link key={w.id} to={`/communities/${w.id}`}>
                <Card className="p-5 h-full hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                      <Building2 className="text-indigo-600 dark:text-indigo-300" size={20} />
                    </div>
                    <Badge tone="indigo">{w.my_role}</Badge>
                  </div>
                  <h3 className="mt-3 font-bold text-gray-900 dark:text-white">{w.name}</h3>
                  {w.event_title && <p className="text-xs text-gray-400">for {w.event_title}</p>}
                  <div className="flex gap-4 mt-3 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1"><Building2 size={14} /> {w.metrics?.rooms ?? 0} rooms</span>
                    <span className="flex items-center gap-1"><Users size={14} /> {w.metrics?.members ?? 0}</span>
                    <span>{w.metrics?.completion ?? 0}% done</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunitiesPage;
